import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Play, Upload } from 'lucide-react';
import { DataTable, type Column } from '../components/data-table';
import { PageHeader, Row, Section, Select } from '../components/form';
import { Alert, Badge, Button, Input } from '../components/ui';
import { ApiError, api, csrfToken } from '../lib/api';

interface MediaFile {
  id: string;
  kind: 'greeting' | 'announcement' | 'moh' | 'system';
  name: string;
  originalFilename: string | null;
  asteriskBasename: string;
  durationMs: number | null;
  createdAt: string;
}

const KINDS = [
  { value: 'greeting', label: 'Greeting' },
  { value: 'announcement', label: 'Announcement' },
  { value: 'moh', label: 'Music on hold' },
] as const;

const KIND_LABEL: Record<string, string> = {
  greeting: 'Greeting',
  announcement: 'Announcement',
  moh: 'Music on hold',
  system: 'System',
};

function duration(ms: number | null): string {
  if (ms === null) return '—';
  const seconds = Math.round(ms / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * Prompts and hold music.
 *
 * Every upload is transcoded to both 8 kHz and 16 kHz so Asterisk can match the negotiated codec;
 * a browser call gets the wideband file rather than a downsampled telephone-quality one. That
 * happens server-side — nothing here asks the admin to think about sample rates.
 */
export function Prompts() {
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const { data: files = [] } = useQuery({
    queryKey: ['media'],
    queryFn: () => api.get<MediaFile[]>('/api/v1/media'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/v1/media/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['media'] }),
    onError: (err) => setError(err instanceof Error ? err.message : 'delete failed'),
  });

  const play = (file: MediaFile) => {
    const element = audioRef.current;
    if (!element) return;
    if (playing === file.id) {
      element.pause();
      setPlaying(null);
      return;
    }
    element.src = `/api/v1/media/${file.id}/audio`;
    setPlaying(file.id);
    void element.play().catch(() => setPlaying(null));
  };

  const columns: Array<Column<MediaFile>> = [
    {
      key: 'name',
      header: 'Name',
      sort: (a, b) => a.name.localeCompare(b.name),
      render: (f) => <span className="font-medium">{f.name}</span>,
    },
    {
      key: 'kind',
      header: 'Type',
      sort: (a, b) => a.kind.localeCompare(b.kind),
      render: (f) => <Badge>{KIND_LABEL[f.kind] ?? f.kind}</Badge>,
    },
    {
      key: 'duration',
      header: 'Length',
      sort: (a, b) => (a.durationMs ?? 0) - (b.durationMs ?? 0),
      render: (f) => <span className="tabular-nums">{duration(f.durationMs)}</span>,
    },
    {
      key: 'basename',
      header: 'Dialplan name',
      render: (f) => (
        <code className="text-xs text-muted-foreground">klink/{f.asteriskBasename}</code>
      ),
    },
    {
      key: 'play',
      header: 'Preview',
      align: 'right',
      render: (f) => (
        <button
          onClick={() => play(f)}
          className="rounded-[--radius] p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label={`Play ${f.name}`}
          title="Play"
        >
          <Play size={15} className={playing === f.id ? 'text-primary' : undefined} />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Prompts and music"
        description="Audio for IVR menus, announcements and hold music."
      />

      <audio ref={audioRef} onEnded={() => setPlaying(null)} className="hidden" />

      {error && <Alert>{error}</Alert>}

      <UploadForm
        uploading={uploading}
        setUploading={setUploading}
        onError={setError}
        onUploaded={() => {
          setError(null);
          void queryClient.invalidateQueries({ queryKey: ['media'] });
        }}
      />

      <DataTable
        rows={files}
        columns={columns}
        rowKey={(f) => f.id}
        search={(f) => `${f.name} ${f.asteriskBasename} ${f.originalFilename ?? ''}`}
        searchPlaceholder="Search prompts"
        onDelete={(rows) => rows.forEach((r) => remove.mutate(r.id))}
        deleteLabel={(rows) =>
          rows.length === 1
            ? `Delete "${rows[0]!.name}"? Any IVR or route still pointing at it will fall silent.`
            : `Delete ${rows.length} files? Anything still pointing at them will fall silent.`
        }
        empty="No prompts yet. Upload one to use it as an IVR greeting."
      />
    </div>
  );
}

function UploadForm({
  uploading,
  setUploading,
  onUploaded,
  onError,
}: {
  uploading: boolean;
  setUploading: (v: boolean) => void;
  onUploaded: () => void;
  onError: (message: string) => void;
}) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState<string>('greeting');
  const [file, setFile] = useState<File | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!file) return;

    setUploading(true);
    try {
      const body = new FormData();
      // Appended before the file so the server has parsed them by the time it reads the stream.
      body.append('name', name || file.name.replace(/\.[^.]+$/, ''));
      body.append('kind', kind);
      body.append('file', file);

      const token = csrfToken();
      const response = await fetch('/api/v1/media', {
        method: 'POST',
        credentials: 'same-origin',
        ...(token ? { headers: { 'x-csrf-token': token } } : {}),
        body,
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { error?: string };
        throw new ApiError(response.status, payload.error ?? 'upload failed', payload);
      }

      setName('');
      setFile(null);
      onUploaded();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={(e) => void submit(e)}>
      <Section title="Upload" description="WAV, MP3, M4A or OGG. Converted automatically.">
        <div className="grid gap-4 sm:grid-cols-3">
          <Row label="Name" required hint="Shown in destination pickers.">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={file ? file.name.replace(/\.[^.]+$/, '') : 'Main greeting'}
            />
          </Row>

          <Row label="Type" required>
            <Select value={kind} onChange={setKind}>
              {KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </Select>
          </Row>

          <Row label="File" required>
            <input
              type="file"
              accept="audio/*,.wav,.mp3,.m4a,.ogg"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="w-full rounded-[--radius] border border-input bg-background px-3 py-1.5 text-sm file:mr-3 file:rounded file:border-0 file:bg-muted file:px-2 file:py-1 file:text-sm"
            />
          </Row>
        </div>

        <div className="mt-4">
          <Button type="submit" disabled={!file || uploading}>
            <Upload size={14} />
            {uploading ? 'Uploading…' : 'Upload'}
          </Button>
        </div>
      </Section>
    </form>
  );
}
